import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  ReactNode,
} from 'react';
import type { Exercise, ExerciseFilters } from '../../exercises/types/exercise';
import type { DailyRoutine, RoutineDifficulty } from '../../routines/types/routine';
import { ApiService } from '../../exercises/services/api';
import { RoutineService } from '../../routines/services/routineService';
import { mensajeDeFallo } from '../utils/red';

// 🚀 Estado global de la aplicación
interface AppState {
  // Global app state
  isInitialized: boolean;
  initializationProgress: number;
  
  // Exercises data
  exercises: Exercise[];
  exercisesLoaded: boolean;
  exerciseFilters: ExerciseFilters;
  exerciseCache: Map<string, Exercise>;
  
  // Filter options data
  categories: string[];
  muscleGroups: string[];
  equipment: string[];
  filterOptionsLoaded: boolean;
  
  // Routines data
  currentRoutine: DailyRoutine | null;
  routineCache: Map<string, DailyRoutine>;
  routinesLoaded: boolean;
  
  // Loading states
  isLoadingExercises: boolean;
  isLoadingRoutines: boolean;
  
  // Error states
  exercisesError: string | null;
  routinesError: string | null;
}

type AppAction =
  // Global initialization actions
  | { type: 'SET_INITIALIZED'; payload: boolean }
  | { type: 'SET_INITIALIZATION_PROGRESS'; payload: number }
  
  // Exercise actions
  | { type: 'SET_EXERCISES'; payload: Exercise[] }
  | { type: 'SET_EXERCISE_FILTERS'; payload: ExerciseFilters }
  | { type: 'ADD_EXERCISE_TO_CACHE'; payload: Exercise }
  | { type: 'SET_EXERCISES_LOADING'; payload: boolean }
  | { type: 'SET_EXERCISES_ERROR'; payload: string | null }
  
  // Filter options actions
  | { type: 'SET_CATEGORIES'; payload: string[] }
  | { type: 'SET_MUSCLE_GROUPS'; payload: string[] }
  | { type: 'SET_EQUIPMENT'; payload: string[] }
  | { type: 'SET_FILTER_OPTIONS_LOADED'; payload: boolean }
  
  // Routine actions
  | { type: 'SET_CURRENT_ROUTINE'; payload: DailyRoutine | null }
  // `key` guarda además la rutina bajo otra clave (p. ej. `date:2026-09-22`),
  // para que buscarla por fecha no tenga que volver a pedirla.
  | { type: 'ADD_ROUTINE_TO_CACHE'; payload: DailyRoutine; key?: string }
  | { type: 'SET_ROUTINES_LOADING'; payload: boolean }
  | { type: 'SET_ROUTINES_LOADED'; payload: boolean }
  | { type: 'SET_ROUTINES_ERROR'; payload: string | null };

// 📦 Estado inicial
const initialState: AppState = {
  isInitialized: false,
  initializationProgress: 0,
  
  exercises: [],
  exercisesLoaded: false,
  exerciseFilters: {},
  exerciseCache: new Map(),
  
  categories: [],
  muscleGroups: [],
  equipment: [],
  filterOptionsLoaded: false,
  
  currentRoutine: null,
  routineCache: new Map(),
  routinesLoaded: false,
  
  isLoadingExercises: false,
  isLoadingRoutines: false,
  
  exercisesError: null,
  routinesError: null,
};

// 🔧 Reducer para manejar estado
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    // Global initialization actions
    case 'SET_INITIALIZED':
      return {
        ...state,
        isInitialized: action.payload,
      };
    
    case 'SET_INITIALIZATION_PROGRESS':
      return {
        ...state,
        initializationProgress: action.payload,
      };

    // Exercise cases
    case 'SET_EXERCISES':
      return {
        ...state,
        exercises: action.payload,
        exercisesLoaded: true,
        exercisesError: null,
      };
    
    case 'SET_EXERCISE_FILTERS':
      return {
        ...state,
        exerciseFilters: action.payload,
      };
    
    case 'ADD_EXERCISE_TO_CACHE': {
      const newExerciseCache = new Map(state.exerciseCache);
      newExerciseCache.set(action.payload.id, action.payload);
      return {
        ...state,
        exerciseCache: newExerciseCache,
      };
    }
    
    case 'SET_EXERCISES_LOADING':
      return {
        ...state,
        isLoadingExercises: action.payload,
      };
    
    case 'SET_EXERCISES_ERROR':
      return {
        ...state,
        exercisesError: action.payload,
        isLoadingExercises: false,
      };
    
    // Filter options cases
    case 'SET_CATEGORIES':
      return {
        ...state,
        categories: action.payload,
      };
    
    case 'SET_MUSCLE_GROUPS':
      return {
        ...state,
        muscleGroups: action.payload,
      };
    
    case 'SET_EQUIPMENT':
      return {
        ...state,
        equipment: action.payload,
      };
    
    case 'SET_FILTER_OPTIONS_LOADED':
      return {
        ...state,
        filterOptionsLoaded: action.payload,
      };
    
    // Routine cases
    case 'SET_CURRENT_ROUTINE':
      return {
        ...state,
        currentRoutine: action.payload,
        routinesLoaded: true,
        routinesError: null,
      };
    
    case 'ADD_ROUTINE_TO_CACHE': {
      const newRoutineCache = new Map(state.routineCache);
      newRoutineCache.set(action.payload.id, action.payload);
      if (action.key) {
        newRoutineCache.set(action.key, action.payload);
      }
      return {
        ...state,
        routineCache: newRoutineCache,
        currentRoutine: action.payload,
      };
    }
    
    case 'SET_ROUTINES_LOADING':
      return {
        ...state,
        isLoadingRoutines: action.payload,
      };
    
    case 'SET_ROUTINES_LOADED':
      return {
        ...state,
        routinesLoaded: action.payload,
      };
    
    case 'SET_ROUTINES_ERROR':
      return {
        ...state,
        routinesError: action.payload,
        isLoadingRoutines: false,
      };
    
    default:
      return state;
  }
}

// 🎯 Contexto principal
interface AppContextType extends AppState {
  // Exercise methods
  loadExercises: (filters?: ExerciseFilters, forceReload?: boolean) => Promise<void>;
  updateExerciseFilters: (filters: ExerciseFilters) => void;
  getExerciseFromCache: (id: string) => Promise<Exercise>;
  
  // Filter options methods
  loadFilterOptions: (forceReload?: boolean) => Promise<void>;
  
  // Routine methods
  loadTodayRoutine: (
    forceReload?: boolean,
    difficulty?: RoutineDifficulty,
  ) => Promise<void>;
  loadRoutineById: (id: string, forceReload?: boolean) => Promise<void>;
  loadRoutineByDate: (date: string, forceReload?: boolean) => Promise<void>;
  loadRoutineByWeekday: (
    dayOfWeek: number,
    difficulty?: RoutineDifficulty,
    forceReload?: boolean,
  ) => Promise<void>;
  getRoutineFromCache: (id: string) => DailyRoutine | null;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export { AppContext };

// 🏗️ Provider del contexto
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  /**
   * El estado, leído por referencia dentro de los cargadores.
   *
   * Los cargadores tienen que ver el estado más reciente (para no repetir una
   * petición que ya se hizo) pero **no pueden cambiar de identidad por ello**.
   * Cuando la cambiaban, cualquier `useEffect` que dependiera de uno de ellos
   * —`RoutinesContainer` depende de `loadTodayRoutine`— se volvía a ejecutar en
   * cada render. Si la rutina cargaba bien no se notaba: el cargador salía por
   * la rama de cache sin tocar el estado y React se paraba. Pero si fallaba, el
   * estado quedaba sin rutina, el cargador no podía salir por cache, hacía
   * `dispatch` + `fetch`, el `dispatch` provocaba otro render, y vuelta a
   * empezar: bucle infinito de renders y peticiones a partir de un solo fallo.
   *
   * Con la referencia, las funciones se crean una vez (`useCallback` sin
   * dependencias) y siguen viendo el estado al día.
   */
  const stateRef = useRef(state);
  stateRef.current = state;

  // 📋 Métodos para ejercicios
  const loadExercises = useCallback(async (
    filters?: ExerciseFilters,
    forceReload = false,
  ) => {
    const estado = stateRef.current;
    const filtersToUse = filters || estado.exerciseFilters;

    // Si ya están cargados y no forzamos recarga, no hacer nada
    if (estado.exercisesLoaded && !forceReload && !filters) {
      return;
    }

    dispatch({ type: 'SET_EXERCISES_LOADING', payload: true });
    
    try {
      const exercises = await ApiService.getExercises(filtersToUse);
      dispatch({ type: 'SET_EXERCISES', payload: exercises });
      
      // Actualizar filtros si se proporcionaron
      if (filters) {
        dispatch({ type: 'SET_EXERCISE_FILTERS', payload: filtersToUse });
      }
    } catch (error) {
      console.error('Error loading exercises:', error);
      dispatch({
        type: 'SET_EXERCISES_ERROR',
        payload: mensajeDeFallo(error, 'No se han podido cargar los ejercicios.'),
      });
    } finally {
      dispatch({ type: 'SET_EXERCISES_LOADING', payload: false });
    }
  }, []);

  const updateExerciseFilters = useCallback((filters: ExerciseFilters) => {
    dispatch({ type: 'SET_EXERCISE_FILTERS', payload: filters });
    loadExercises(filters);
  }, [loadExercises]);

  const getExerciseFromCache = useCallback(async (id: string): Promise<Exercise> => {
    // Primero revisar el cache
    const cachedExercise = stateRef.current.exerciseCache.get(id);
    if (cachedExercise) {
      return cachedExercise;
    }

    // Si no está en cache, hacer petición
    try {
      const exercise = await ApiService.getExercise(id);
      dispatch({ type: 'ADD_EXERCISE_TO_CACHE', payload: exercise });
      return exercise;
    } catch (error) {
      console.error(`Error loading exercise ${id}:`, error);
      throw error;
    }
  }, []);

  // 🏷️ Métodos para opciones de filtros
  const loadFilterOptions = useCallback(async (forceReload = false) => {
    if (stateRef.current.filterOptionsLoaded && !forceReload) {
      return;
    }

    try {
      const [categories, muscleGroups, equipment] = await Promise.all([
        ApiService.getCategories(),
        ApiService.getMuscleGroups(),
        ApiService.getEquipment(),
      ]);

      dispatch({ type: 'SET_CATEGORIES', payload: categories });
      dispatch({ type: 'SET_MUSCLE_GROUPS', payload: muscleGroups });
      dispatch({ type: 'SET_EQUIPMENT', payload: equipment });
      dispatch({ type: 'SET_FILTER_OPTIONS_LOADED', payload: true });
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  }, []);

  // 💪 Métodos para rutinas
  const loadTodayRoutine = useCallback(async (
    forceReload = false,
    difficulty?: RoutineDifficulty,
  ) => {
    const estado = stateRef.current;

    // ⚠️ IMPORTANTE: Solo usar cache para rutinas exitosas, no para null/vacías
    // El cache es por rutina, no por nivel: si la cacheada es de otro nivel hay
    // que recargar aunque no se fuerce, o el selector no haría nada visible.
    const cachedMatches =
      difficulty === undefined ||
      estado.currentRoutine?.difficulty === difficulty;

    if (!forceReload && estado.routinesLoaded && estado.currentRoutine && cachedMatches) {
      // La rutina que se pide ya está cargada, así que no hay que pedirla.
      // Pero puede quedar un error de un intento anterior: pruebas el nivel
      // avanzado, falla, y al volver al que tenías se entraba por aquí sin
      // tocar nada. La pantalla seguía enseñando el error rojo con la rutina
      // buena ya en la mano, y no había forma de salir cambiando de nivel.
      if (estado.routinesError) {
        dispatch({ type: 'SET_ROUTINES_ERROR', payload: null });
      }
      return;
    }

    // El orden importa: SET_ROUTINES_ERROR apaga isLoadingRoutines, así que
    // limpiar el error después de encender la carga dejaba la pantalla sin
    // spinner en cada recarga.
    dispatch({ type: 'SET_ROUTINES_ERROR', payload: null });
    dispatch({ type: 'SET_ROUTINES_LOADING', payload: true });

    try {
      const routine = await RoutineService.getTodayRoutine(difficulty);
      
      if (routine) {
        dispatch({ type: 'ADD_ROUTINE_TO_CACHE', payload: routine });
        dispatch({ type: 'SET_ROUTINES_LOADED', payload: true });
      } else {
        // NO cachear resultados null - permitir reintentos
        dispatch({ type: 'SET_CURRENT_ROUTINE', payload: null });
        // NO marcar como "loaded" para permitir nuevas peticiones
      }
    } catch (error) {
      console.error('Error loading today routine:', error);
      dispatch({
        type: 'SET_ROUTINES_ERROR',
        payload: mensajeDeFallo(error, 'No se ha podido cargar la rutina de hoy.'),
      });
    } finally {
      dispatch({ type: 'SET_ROUTINES_LOADING', payload: false });
    }
  }, []);

  /**
   * 📅 La rutina del gimnasio para un día de la semana.
   *
   * Sustituye a pedirla por fecha: las rutinas son fijas, así que cada lunes
   * es el mismo lunes y elegir el día es lo que de verdad se está eligiendo.
   * La cache va por día **y** nivel, porque son filas distintas.
   */
  const loadRoutineByWeekday = useCallback(async (
    dayOfWeek: number,
    difficulty?: RoutineDifficulty,
    forceReload = false,
  ) => {
    const cacheKey = `weekday:${dayOfWeek}:${difficulty ?? 'intermediate'}`;

    if (!forceReload) {
      const cacheada = stateRef.current.routineCache.get(cacheKey);
      if (cacheada) {
        dispatch({ type: 'SET_CURRENT_ROUTINE', payload: cacheada });
        // Puede quedar el error de otro día o de otro nivel: se limpia al
        // entrar por cache, o la pantalla enseñaría el error rojo con la
        // rutina buena ya en la mano.
        if (stateRef.current.routinesError) {
          dispatch({ type: 'SET_ROUTINES_ERROR', payload: null });
        }
        return;
      }
    }

    // El orden importa: SET_ROUTINES_ERROR apaga isLoadingRoutines.
    dispatch({ type: 'SET_ROUTINES_ERROR', payload: null });
    dispatch({ type: 'SET_ROUTINES_LOADING', payload: true });

    try {
      const routine = await RoutineService.getRoutineByWeekday(
        dayOfWeek,
        difficulty,
      );
      if (routine) {
        dispatch({ type: 'ADD_ROUTINE_TO_CACHE', payload: routine, key: cacheKey });
        dispatch({ type: 'SET_ROUTINES_LOADED', payload: true });
      } else {
        // Sin cachear: un día sin rutina tiene que poder reintentarse.
        dispatch({ type: 'SET_CURRENT_ROUTINE', payload: null });
      }
    } catch (error) {
      console.error(`Error loading routine for weekday ${dayOfWeek}:`, error);
      dispatch({
        type: 'SET_ROUTINES_ERROR',
        payload: mensajeDeFallo(error, 'No se ha podido cargar la rutina del día.'),
      });
    } finally {
      dispatch({ type: 'SET_ROUTINES_LOADING', payload: false });
    }
  }, []);

  const loadRoutineById = useCallback(async (id: string, forceReload = false) => {
    // Revisar cache primero
    const cachedRoutine = stateRef.current.routineCache.get(id);
    if (cachedRoutine && !forceReload) {
      dispatch({ type: 'SET_CURRENT_ROUTINE', payload: cachedRoutine });
      return;
    }

    dispatch({ type: 'SET_ROUTINES_LOADING', payload: true });

    try {
      const routine = await RoutineService.getRoutineById(id);
      if (routine) {
        dispatch({ type: 'ADD_ROUTINE_TO_CACHE', payload: routine });
      } else {
        dispatch({ type: 'SET_CURRENT_ROUTINE', payload: null });
      }
    } catch (error) {
      console.error(`Error loading routine ${id}:`, error);
      dispatch({
        type: 'SET_ROUTINES_ERROR',
        payload: mensajeDeFallo(error, 'No se ha podido cargar la rutina.'),
      });
    } finally {
      dispatch({ type: 'SET_ROUTINES_LOADING', payload: false });
    }
  }, []);

  const getRoutineFromCache = useCallback((id: string): DailyRoutine | null => {
    return stateRef.current.routineCache.get(id) || null;
  }, []);

  // 📅 Cargar rutina por fecha específica
  const loadRoutineByDate = useCallback(async (date: string, forceReload = false) => {
    // Usar la fecha como clave de cache
    const cacheKey = `date:${date}`;

    // ⚠️ IMPORTANTE: Solo usar cache para rutinas exitosas, no para null/vacías
    if (!forceReload) {
      const cachedRoutine = stateRef.current.routineCache.get(cacheKey);
      if (cachedRoutine) {
        // Solo usar cache si hay una rutina válida
        dispatch({ type: 'SET_CURRENT_ROUTINE', payload: cachedRoutine });
        return;
      }
    }

    // Igual que en loadTodayRoutine: limpiar el error antes de encender la
    // carga, porque SET_ROUTINES_ERROR apaga isLoadingRoutines.
    dispatch({ type: 'SET_ROUTINES_ERROR', payload: null });
    dispatch({ type: 'SET_ROUTINES_LOADING', payload: true });

    try {
      const routine = await RoutineService.getRoutineByDate(date);

      if (routine) {
        // Solo cachear rutinas exitosas, por id y por fecha. Antes se construía
        // un Map con las dos claves que se descartaba sin usar, así que el
        // cache por fecha nunca llegó a existir y cada cambio de día repetía la
        // petición.
        dispatch({ type: 'ADD_ROUTINE_TO_CACHE', payload: routine, key: cacheKey });
      } else {
        // NO cachear resultados null - siempre permitir reintentos
        dispatch({ type: 'SET_CURRENT_ROUTINE', payload: null });
      }
    } catch (error) {
      console.error(`Error loading routine for date ${date}:`, error);
      dispatch({
        type: 'SET_ROUTINES_ERROR',
        payload: mensajeDeFallo(error, `No se ha podido cargar la rutina del ${date}.`),
      });
    } finally {
      dispatch({ type: 'SET_ROUTINES_LOADING', payload: false });
    }
  }, []);

  // 🚀 Carga inicial de datos esenciales
  useEffect(() => {
    const initializeApp = async () => {
      // Cargar opciones de filtros de forma prioritaria
      await loadFilterOptions();

      // Cargar ejercicios con filtros por defecto
      await loadExercises();

      // La rutina NO se pide aquí. Depende del día y del nivel que elija cada
      // quien, así que la pide la pantalla de rutinas al abrirse; traerla al
      // arrancar era una petición de más para todo el que no entra ahí, y
      // encima con el día y el nivel equivocados.
    };

    initializeApp();
    // Los dos cargadores tienen identidad estable, así que esto sigue
    // ejecutándose una sola vez al montar, ahora sin silenciar el linter.
  }, [loadFilterOptions, loadExercises]);

  // Sin memoizar, este objeto era nuevo en cada render y arrastraba a todos
  // los consumidores del contexto con él.
  const contextValue: AppContextType = useMemo(() => ({
    ...state,
    loadExercises,
    updateExerciseFilters,
    getExerciseFromCache,
    loadFilterOptions,
    loadTodayRoutine,
    loadRoutineById,
    loadRoutineByDate,
    loadRoutineByWeekday,
    getRoutineFromCache,
  }), [
    state,
    loadExercises,
    updateExerciseFilters,
    getExerciseFromCache,
    loadFilterOptions,
    loadTodayRoutine,
    loadRoutineById,
    loadRoutineByDate,
    loadRoutineByWeekday,
    getRoutineFromCache,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};
